import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Avatar,
    Box,
    Typography,
    Chip,
} from '@mui/material';
import { PersonAddAlt as PersonAddAltIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';

interface Resource {
    id: number;
    name: string;
    admin: number;
    active: number;
}

interface AddProxyModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
    selectedProxy: number;
    onProxyChange: (proxyId: number) => void;
    resources: Resource[];
    currentUserId: number | null;
}

export const AddProxyModal = ({
    open,
    onClose,
    onSubmit,
    selectedProxy,
    onProxyChange,
    resources,
    currentUserId,
}: AddProxyModalProps) => {
    const { LL } = useI18nContext();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                        <PersonAddAltIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {LL.addProxyModal.title()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {LL.addProxyModal.subtitle()}
                        </Typography>
                    </Box>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mt: 3 }}>
                    <InputLabel>{LL.addProxyModal.selectProxy()}</InputLabel>
                    <Select
                        value={selectedProxy}
                        onChange={(e) => onProxyChange(e.target.value as number)}
                        label={LL.addProxyModal.selectProxy()}
                    >
                        <MenuItem value={0} disabled>
                            <em>{LL.addProxyModal.selectProxyHelperText()}</em>
                        </MenuItem>
                        {resources
                            .filter((r) => r.id !== currentUserId && r.active === 1)
                            .map((resource) => (
                                <MenuItem key={resource.id} value={resource.id}>
                                    <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                                        <Avatar
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                fontSize: '0.75rem',
                                                bgcolor:
                                                    resource.admin === 1
                                                        ? 'warning.main'
                                                        : 'primary.main',
                                            }}
                                        >
                                            {resource.name.charAt(0)}
                                        </Avatar>
                                        <span>{resource.name}</span>
                                        {resource.admin === 1 && (
                                            <Chip
                                                label={LL.userCard.admin()}
                                                size="small"
                                                color="warning"
                                            />
                                        )}
                                    </Stack>
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} variant="outlined">
                    {LL.common.cancel()}
                </Button>
                <Button
                    onClick={onSubmit}
                    variant="contained"
                    disabled={!selectedProxy}
                    startIcon={<PersonAddAltIcon />}
                >
                    {LL.userCard.addProxy()}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
